import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getDownloadURL, getMetadata, ref } from 'firebase/storage';

import Modal from './Modal';
import Spinner from './Spinner';
import { storage, authReady } from '../../config/firebase';
import { adminApi } from '../../config/axios';
import { useGlobal } from '../../contexts/GlobalContext';

/**
 * Triggers a screenshot on the device (POST /admin/device/{id}/command) and
 * polls Firebase Storage at screenshots/{serial}.png until the freshly
 * uploaded file appears (or the timeout expires). The Android player
 * (Repository.kt → sendScreenshotToFirebase) overwrites the same object on
 * every capture, so we use the `updated` metadata to know when the new file
 * has landed.
 */
export default function ScreenshotModal({ open, onClose, device }) {
  const { showToast } = useGlobal();
  const [imageUrl, setImageUrl]   = useState(null);
  const [capturedAt, setCapturedAt] = useState(null);
  const [status, setStatus]       = useState('idle'); // idle | capturing | loading | ready | error
  const [error, setError]         = useState(null);
  const pollRef = useRef({ cancelled: false });

  const serial = device?.serial_name;
  const deviceLabel = device?.name ?? serial ?? 'Device';

  const fetchCurrent = useCallback(async () => {
    if (!serial) return null;
    await authReady; // Storage rules gate /screenshots/* on auth != null
    const imageRef = ref(storage, `screenshots/${serial}.png`);
    const [url, metadata] = await Promise.all([
      getDownloadURL(imageRef),
      getMetadata(imageRef),
    ]);
    return {
      url: `${url}&_ts=${Date.now()}`, // cache-bust
      updated: metadata?.updated ? new Date(metadata.updated) : null,
    };
  }, [serial]);

  const loadLatest = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const { url, updated } = await fetchCurrent();
      setImageUrl(url);
      setCapturedAt(updated);
      setStatus('ready');
    } catch (err) {
      setImageUrl(null);
      setError('No screenshot has been captured for this device yet.');
      setStatus('error');
    }
  }, [fetchCurrent]);

  const capture = useCallback(async () => {
    if (!device?.id) return;
    setStatus('capturing');
    setError(null);

    // Record the existing object's `updated` timestamp so we can detect when
    // the new capture has replaced it; if no object exists yet, baseline is
    // null and any successful fetch is "new enough".
    let baseline = null;
    try { baseline = (await fetchCurrent())?.updated ?? null; } catch { /* first-ever capture */ }

    try {
      await adminApi.post(`/device/${device.id}/command`, { command: 'screenshot' });
    } catch (err) {
      setStatus('error');
      setError('Could not reach the device. Is it online?');
      return;
    }

    showToast('info', 'Capturing', `${deviceLabel} is taking a screenshot…`);

    // Poll Storage every 1.5s for up to 20s waiting for the device to upload.
    pollRef.current.cancelled = false;
    const deadline = Date.now() + 20_000;
    while (Date.now() < deadline) {
      if (pollRef.current.cancelled) return;
      try {
        const latest = await fetchCurrent();
        const isNewer = latest?.updated && (!baseline || latest.updated > baseline);
        if (isNewer) {
          setImageUrl(latest.url);
          setCapturedAt(latest.updated);
          setStatus('ready');
          return;
        }
      } catch { /* not yet — keep polling */ }
      await new Promise(r => setTimeout(r, 1500));
    }

    // Timed out — fall back to whatever's there so the user still sees something.
    try {
      const fallback = await fetchCurrent();
      setImageUrl(fallback?.url ?? null);
      setCapturedAt(fallback?.updated ?? null);
      setStatus(fallback?.url ? 'ready' : 'error');
      if (!fallback?.url) setError('Device did not upload a screenshot in time.');
    } catch {
      setStatus('error');
      setError('Device did not upload a screenshot in time.');
    }
  }, [device?.id, deviceLabel, fetchCurrent, showToast]);

  // On open: try to show whatever is already in Storage so the user has an
  // initial reference frame; if nothing exists, prompt them to capture.
  useEffect(() => {
    if (!open) {
      pollRef.current.cancelled = true;
      setImageUrl(null);
      setCapturedAt(null);
      setStatus('idle');
      setError(null);
      return;
    }
    if (serial) loadLatest();
  }, [open, serial, loadLatest]);

  const download = () => {
    if (!imageUrl) return;
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = `${serial ?? 'device'}-screenshot.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`📸 Screenshot — ${deviceLabel}`}
      size="xl"
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={download}
            disabled={!imageUrl}
          >
            Download
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={capture}
            disabled={status === 'capturing'}
          >
            {status === 'capturing' ? (
              <span className="inline-flex items-center gap-2"><Spinner size="sm" /> Capturing…</span>
            ) : (
              <span>🔄 Capture New</span>
            )}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
        </>
      }
    >
      <div className="space-y-3">
        {capturedAt && (
          <p className="text-xs text-text-muted">
            Captured: {capturedAt.toLocaleString()}
          </p>
        )}

        <div className="aspect-video bg-black/40 rounded-xl flex items-center justify-center border border-white/[0.06] overflow-hidden">
          {status === 'capturing' || status === 'loading' ? (
            <div className="flex flex-col items-center gap-2 text-text-muted text-sm">
              <Spinner size="lg" />
              <span>{status === 'capturing' ? 'Asking the device to capture…' : 'Loading screenshot…'}</span>
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={`${deviceLabel} screen`}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="text-center text-text-muted text-sm px-6">
              <p>{error ?? 'No screenshot available.'}</p>
              <p className="mt-2">Click <strong>Capture New</strong> to take one now.</p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
